import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useStmAnalyticsEvent } from '@dni-analysis'
import { StmSectionName } from '@dni-analysis/types/fastpass-ga4-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { FpB2bProductCatalogProvider } from '../../../fp-b2b-shared/components/ProductCatalogProvider/ProductCatalogProvider'
import { DIALOG_PRODUCT_CART, DialogProductCart, type DialogProductCartProps } from '../../components/DialogProductCart'
import { useStmBrandStore } from '../../hooks'
import { useBrandTopSellers } from '../hooks/use-brand-top-sellers'
import { type ProductCardEntry } from '../types/product-card-entry'
import { ProductList } from './ProductList'
import { RankedProductCard } from './RankedProductCard'

export const BrandHotProductSectionContentSkeleton = () => (
  <Box flex={1} p={3} minWidth={0}>
    <ProductList isLoading={true} hasSubtitleChips />
  </Box>
)

interface BrandHotProductSectionContentProps {
  campaign: CrossBrandMallHomeCampaign
  currency: string
  basePath: string
}

export const BrandHotProductSectionContent = (props: BrandHotProductSectionContentProps) => {
  const { campaign, currency, basePath } = props

  const { productCardEntries, isProductsLoading } = useBrandTopSellers({ campaign })

  const brandInfo = useStmBrandStore(state =>
    state.brands.find(brand => brand.campaignCode === campaign.code),
  )
  const dialogKey = `${DIALOG_PRODUCT_CART}_HOT_PRODUCT_${campaign.code}`
  const { openDialog } = useDialog<DialogProductCartProps>(dialogKey)
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaign.code })
  const { sendCrossBrandAddToCartEvent } = useStmAnalyticsEvent({ userId: userInfo?.platform_uid ?? '' })

  if (!brandInfo) return null

  const handleOpenCartDialog = (entry: ProductCardEntry) => {
    sendCrossBrandAddToCartEvent({ projectCode: campaign.project_code, sectionName: StmSectionName.HOT_SELLING })
    openDialog({
      title: entry.title,
      thumbnail: entry.imageUrl,
      variations: entry.variations,
      basePath,
    })
  }

  return (
    <FpB2bProductCatalogProvider
      platform={Platform.STM}
      campaignId={brandInfo.campaignId}
      campaignCode={brandInfo.campaignCode}
      projectCode={brandInfo.projectCode}
      countryCode={brandInfo.countryCode}
      currency={currency}
    >
      <Box flex={1} p={3} minWidth={0}>
        <ProductList isLoading={isProductsLoading} hasSubtitleChips>
          {productCardEntries.map((entry, index) => (
            <RankedProductCard
              key={entry.key}
              rank={index + 1}
              entry={entry}
              onSelect={handleOpenCartDialog}
            />
          ))}
        </ProductList>
      </Box>
      <DialogProductCart dialogKey={dialogKey} />
    </FpB2bProductCatalogProvider>
  )
}
